import React, { useEffect, useState } from "react";
import Select from "react-select";
import { FaEdit } from "react-icons/fa";
import api from "../../api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TeacherPermissionPage = () => {

    const [teachers, setTeachers] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [assignedList, setAssignedList] = useState([]);

    const [form, setForm] = useState({
        teacher_user_id: "",
        permission_ids: []
    });

    const [editTeacherId, setEditTeacherId] = useState(null);

    // API URLs
    const teacherURL = `/teachers/list`;
    const permissionURL = `/v1/admin/permissions/list`;
    const assignURL = `/v1/admin/teacher-permissions/save`;
    const listURL = `/v1/admin/teacher-permissions/list`;
    const toggleURL = `/v1/admin/teacher-permissions/toggle`;

    // FETCH TEACHERS
    const fetchTeachers = async () => {
        try {
            const res = await api.get(teacherURL);
            setTeachers(res.data?.data || []);
        } catch (err) {
            toast.error("Failed to load teachers ❌");
        }
    };

    // FETCH PERMISSIONS
    const fetchPermissions = async () => {
        try {
            const res = await api.get(permissionURL);
            setPermissions(res.data?.data?.permissions || []);
        } catch (err) {
            toast.error("Failed to load permissions ❌");
        }
    };

    // FETCH ASSIGNED PERMISSION LIST
    const fetchAssignedList = async () => {
        try {
            const res = await api.get(listURL);
            const perms = res.data?.data?.permissions || [];

            const teacherMap = {};

            perms.forEach((perm) => {
                perm.assigned_teachers.forEach((t) => {
                    if (!teacherMap[t.teacher_user_id]) {
                        teacherMap[t.teacher_user_id] = {
                            teacher_user_id: t.teacher_user_id,
                            teacher_name: t.teacher_name,
                            permissions: []
                        };
                    }

                    teacherMap[t.teacher_user_id].permissions.push({
                        id: perm.id,
                        title: perm.title,
                        status: Number(t.status) // FIX: Ensure status is Number (1/0)
                    });
                });
            });

            setAssignedList(Object.values(teacherMap));

        } catch (err) {
            toast.error("Failed to load assigned permissions ❌");
        }
    };

    useEffect(() => {
        fetchTeachers();
        fetchPermissions();
        fetchAssignedList();
    }, []);

    // SELECT TEACHER
    const handleTeacherSelect = (selected) => {
        setForm({
            ...form,
            teacher_user_id: selected ? selected.value : ""
        });
    };

    // SELECT PERMISSIONS
    const handlePermissionSelect = (selectedOptions) => {
        setForm({
            ...form,
            permission_ids: selectedOptions ? selectedOptions.map(o => o.value) : []
        });
    };

    // SAVE PERMISSION ASSIGN
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.teacher_user_id) return toast.error("Select a teacher ❌");
        if (form.permission_ids.length === 0) return toast.error("Select at least one permission ❌");

        try {
            const res = await api.post(assignURL, form);

            if (res.data.status) {
                toast.success("Permissions Assigned ✔");
                setForm({ teacher_user_id: "", permission_ids: [] });
                setEditTeacherId(null);
                fetchAssignedList();
            } else {
                toast.error(res.data.message || "Failed to assign ❌");
            }
        } catch (err) {
            toast.error("Network Error ❌");
        }
    };

    // ENABLE EDIT MODE
    const handleEdit = (row) => {
        setEditTeacherId(row.teacher_user_id);
        setForm({
            teacher_user_id: row.teacher_user_id,
            permission_ids: row.permissions.map((p) => p.id)
        });

        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.info("Edit Mode Enabled ✏️");
    };

    // TOGGLE STATUS
    const togglePermissionStatus = async (teacher_user_id, permission_id, currentStatus) => {

        const newStatus = currentStatus === 1 ? 0 : 1;

        try {
            const res = await api.post(toggleURL, {
                teacher_user_id,
                permission_id,
                status: newStatus
            });

            if (res.data.status) {
                toast.success("Status Updated ✔");
                fetchAssignedList();
            } else {
                toast.error(res.data.message || "Failed to update status ❌");
            }
        } catch (err) {
            toast.error("Network Error ❌");
        }
    };

    // OPTIONS
    const teacherOptions = teachers.map((t) => ({
        value: t.user_id,
        label: t.name
    }));

    const permissionOptions = permissions.map((p) => ({
        value: p.id,
        label: p.title
    }));

    // SWITCH UI
    const switchWrapper = {
        marginBottom: 10,
        cursor: "pointer"
    };

    const toggleSwitch = (active) => ({
        width: "46px",
        height: "24px",
        backgroundColor: active ? "green" : "#bbb",
        borderRadius: "20px",
        position: "relative",
        transition: "0.3s"
    });

    const toggleKnob = (active) => ({
        width: "20px",
        height: "20px",
        background: "#fff",
        borderRadius: "50%",
        position: "absolute",
        top: "2px",
        left: active ? "24px" : "2px",
        transition: "0.3s"
    });

    return (
        <div style={pageContainer}>
            <h2 style={mainTitle}>🧑‍🏫 Teacher Permission Assign</h2>

            {/* FORM */}
            <section style={cardStyle}>
                <h3 style={cardTitle}>{editTeacherId ? "✏️ Edit Assigned Permission" : "➕ Assign Permission"}</h3>

                <form onSubmit={handleSubmit}>
                    <div style={formGrid}>

                        <FormGroup label="Select Teacher">
                            <Select
                                options={teacherOptions}
                                value={teacherOptions.find(o => o.value === form.teacher_user_id) || null}
                                onChange={handleTeacherSelect}
                                isClearable
                                placeholder="Search teacher..."
                            />
                        </FormGroup>

                        <FormGroup label="Select Permissions">
                            <Select
                                options={permissionOptions}
                                value={permissionOptions.filter(o => form.permission_ids.includes(o.value))}
                                onChange={handlePermissionSelect}
                                isMulti
                                placeholder="Search permissions..."
                            />
                        </FormGroup>
                    </div>

                    <div style={{ textAlign: "right" }}>
                        <button type="submit" style={submitBtn}>
                            {editTeacherId ? "Update" : "Assign"}
                        </button>
                    </div>
                </form>
            </section>

            {/* LIST */}
            <section style={cardStyle}>
                <h3 style={cardTitle}>📋 Assigned Permissions</h3>

                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Teacher</th>
                            <th style={thStyle}>Permissions</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {assignedList.map((row, idx) => (
                            <tr key={idx} style={trStyle}>
                                <td style={tdStyle}>{row.teacher_name}</td>

                                <td style={tdStyle}>
                                    {row.permissions.map((p) => (
                                        <div key={p.id}>{p.title}</div>
                                    ))}
                                </td>

                                {/* TOGGLE SWITCH */}
                                <td style={tdStyle}>
                                    {row.permissions.map((p) => (
                                        <div
                                            key={p.id}
                                            style={switchWrapper}
                                            onClick={() =>
                                                togglePermissionStatus(
                                                    row.teacher_user_id,
                                                    p.id,
                                                    p.status
                                                )
                                            }
                                        >
                                            <div style={toggleSwitch(p.status === 1)}>
                                                <div style={toggleKnob(p.status === 1)}></div>
                                            </div>
                                        </div>
                                    ))}
                                </td>

                                <td style={tdCenter}>
                                    <FaEdit
                                        onClick={() => handleEdit(row)}
                                        style={{ cursor: "pointer", color: "#0077cc", fontSize: 18 }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <ToastContainer />
        </div>
    );
};

/* ======================= CSS ========================== */

const pageContainer = {
    padding: "40px 60px",
    maxWidth: "1100px",
    margin: "auto",
    background: "#f5f7fb",
    minHeight: "100vh"
};

const mainTitle = {
    fontSize: "28px",
    fontWeight: "800",
    textAlign: "center",
    marginBottom: "30px"
};

const cardStyle = {
    background: "#fff",
    padding: "35px",
    borderRadius: "16px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
    marginBottom: "35px"
};

const cardTitle = {
    fontSize: "22px",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "22px",
    color: "#0b3d91"
};

const formGrid = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "25px"
};

const submitBtn = {
    padding: "12px 30px",
    background: "#0077cc",
    border: "none",
    color: "#fff",
    borderRadius: "10px",
    fontSize: "17px",
    cursor: "pointer"
};

const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0 10px"
};

const thStyle = {
    padding: "14px",
    background: "#eef4ff",
    textAlign: "center",
    fontWeight: "700"
};

const trStyle = {
    background: "#fff",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    borderRadius: "12px"
};

const tdStyle = {
    padding: "14px 18px",
    fontSize: "14px",
    color: "#333"
};

const tdCenter = { ...tdStyle, textAlign: "center" };

const FormGroup = ({ label, children }) => (
    <div>
        <label style={{ marginBottom: 8, fontWeight: 600 }}>{label}</label>
        {children}
    </div>
);

export default TeacherPermissionPage;
